import { Children, cloneElement, isValidElement } from "react";
import type { ComponentProps, ReactElement, ReactNode } from "react";
import { ContextMenu as C, Dialog as D, DropdownMenu as M, Popover as P, Tooltip as T } from "radix-ui";
import { X } from "lucide-react";
import { cn } from "@/lib/utils";

// ───────────── Diálogo ─────────────
export const Dialog = D.Root;
export const DialogTrigger = D.Trigger;
export const DialogClose = D.Close;

export function DialogContent({
  title,
  description,
  children,
  className,
  wide,
}: {
  title: string;
  description?: ReactNode;
  children: ReactNode;
  className?: string;
  wide?: boolean;
}) {
  return (
    <D.Portal>
      <D.Overlay className="fixed inset-0 z-50 animate-fade-in bg-overlay" />
      <D.Content
        className={cn(
          "fixed left-1/2 top-[12vh] z-50 flex max-h-[80vh] w-[calc(100vw-2rem)] -translate-x-1/2 animate-pop-in flex-col overflow-hidden rounded-xl border border-border bg-surface shadow-[0_24px_64px_-16px_rgba(0,0,0,0.5)]",
          wide ? "max-w-3xl" : "max-w-lg",
          className,
        )}
      >
        <div className="flex items-start justify-between gap-4 px-5 pb-2 pt-5">
          <div>
            <D.Title className="text-[15px] font-semibold tracking-[-0.01em]">{title}</D.Title>
            {description ? (
              <D.Description className="mt-1 text-[13px] text-fg-muted">{description}</D.Description>
            ) : (
              <D.Description className="sr-only">{title}</D.Description>
            )}
          </div>
          <D.Close
            className="-mr-1.5 -mt-1 rounded-md p-1.5 text-fg-subtle transition-colors hover:bg-bg-muted hover:text-fg"
            aria-label="Fechar"
          >
            <X className="h-4 w-4" />
          </D.Close>
        </div>
        <div className="min-h-0 flex-1 overflow-y-auto px-5 pb-5 pt-3">{children}</div>
      </D.Content>
    </D.Portal>
  );
}

export function DialogFooter({ children }: { children: ReactNode }) {
  return (
    <div className="-mx-5 -mb-5 mt-5 flex justify-end gap-2 border-t border-border px-5 py-3">{children}</div>
  );
}

// ───────────── Menu suspenso ─────────────
export const Menu = M.Root;
export const MenuTrigger = M.Trigger;

export function MenuContent({ className, align = "end", ...props }: ComponentProps<typeof M.Content>) {
  return (
    <M.Portal>
      <M.Content
        align={align}
        sideOffset={6}
        className={cn(
          "z-[70] min-w-48 animate-pop-in rounded-lg border border-border bg-surface p-1 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.4)]",
          className,
        )}
        {...props}
      />
    </M.Portal>
  );
}

export function MenuItem({
  className,
  icon,
  shortcut,
  trailing,
  children,
  ...props
}: ComponentProps<typeof M.Item> & {
  icon?: ReactNode;
  shortcut?: string;
  /** Algo à direita do texto (ex.: a seta de "abre no navegador"), sempre na mesma linha. */
  trailing?: ReactNode;
}) {
  // Texto simples: uma linha só (corta com "…"). Conteúdo montado (título + descrição): quebra normal.
  const labelClass = (content: ReactNode) =>
    typeof content === "string" || typeof content === "number"
      ? "min-w-0 flex-1 truncate whitespace-nowrap"
      : "min-w-0 flex-1";
  const iconSlot = icon ? (
    <span className="flex h-4 w-4 shrink-0 items-center justify-center opacity-80">{icon}</span>
  ) : null;
  const shortcutSlot = shortcut ? (
    <span className="ml-4 shrink-0 font-mono text-[11px] opacity-60">{shortcut}</span>
  ) : null;
  const trailingSlot = trailing ? (
    <span className="ml-3 flex h-4 shrink-0 items-center justify-center opacity-60">{trailing}</span>
  ) : null;
  // Com asChild, o filho (um link, por exemplo) vira o item. O ícone e o texto entram dentro dele,
  // com o mesmo alinhamento dos outros itens (antes o ícone sumia e o texto ficava desalinhado).
  if (props.asChild) {
    const child = Children.only(children) as ReactElement<{ children?: ReactNode }>;
    const decorated =
      (iconSlot || shortcutSlot || trailingSlot) && isValidElement(child)
        ? cloneElement(
            child,
            undefined,
            <>
              {iconSlot}
              <span className={labelClass(child.props.children)}>{child.props.children}</span>
              {shortcutSlot}
              {trailingSlot}
            </>,
          )
        : child;
    return (
      <M.Item
        className={cn(
          "flex h-[30px] cursor-default select-none items-center gap-2 rounded-md px-2 text-[13px] text-fg outline-none",
          "data-[highlighted]:bg-bg-emphasis data-[disabled]:opacity-40",
          className,
        )}
        {...props}
      >
        {decorated}
      </M.Item>
    );
  }
  return (
    <M.Item
      className={cn(
        "flex h-[30px] cursor-default select-none items-center gap-2 rounded-md px-2 text-[13px] text-fg outline-none",
        "data-[highlighted]:bg-bg-emphasis data-[disabled]:opacity-40",
        className,
      )}
      {...props}
    >
      {iconSlot}
      <span className={labelClass(children)}>{children}</span>
      {shortcutSlot}
      {trailingSlot}
    </M.Item>
  );
}

export const MenuSeparator = () => <M.Separator className="-mx-1 my-1 h-px bg-border" />;
export const MenuLabel = ({ children }: { children: ReactNode }) => (
  <M.Label className="px-2 py-1.5 text-[11.5px] font-medium text-fg-subtle">{children}</M.Label>
);

// ───────────── Menu de contexto (botão direito) ─────────────
export const ContextMenu = C.Root;
export const ContextMenuTrigger = C.Trigger;

export function ContextMenuContent({ className, ...props }: ComponentProps<typeof C.Content>) {
  return (
    <C.Portal>
      <C.Content
        className={cn(
          "z-[70] min-w-52 animate-pop-in rounded-lg border border-border bg-surface p-1 shadow-[0_12px_32px_-8px_rgba(0,0,0,0.4)]",
          className,
        )}
        {...props}
      />
    </C.Portal>
  );
}

export function ContextMenuItem({
  className,
  icon,
  shortcut,
  children,
  ...props
}: ComponentProps<typeof C.Item> & { icon?: ReactNode; shortcut?: string }) {
  return (
    <C.Item
      className={cn(
        "flex h-7 cursor-default select-none items-center gap-2 rounded-md px-2 text-[12.5px] text-fg outline-none",
        "data-[highlighted]:bg-bg-emphasis data-[disabled]:opacity-40",
        className,
      )}
      {...props}
    >
      {icon ? <span className="flex h-4 w-4 items-center justify-center opacity-80">{icon}</span> : null}
      <span className="flex-1">{children}</span>
      {shortcut ? <span className="font-mono text-[11px] opacity-60">{shortcut}</span> : null}
    </C.Item>
  );
}

export const ContextMenuSeparator = () => <C.Separator className="-mx-1 my-1 h-px bg-border" />;

// ───────────── Popover ─────────────
export const Popover = P.Root;
export const PopoverTrigger = P.Trigger;
export function PopoverContent({ className, ...props }: ComponentProps<typeof P.Content>) {
  return (
    <P.Portal>
      <P.Content
        sideOffset={6}
        className={cn(
          "z-[70] animate-pop-in rounded-lg border border-border bg-surface shadow-[0_12px_32px_-8px_rgba(0,0,0,0.4)] outline-none",
          className,
        )}
        {...props}
      />
    </P.Portal>
  );
}

// ───────────── Dica (tooltip) ─────────────
export const TooltipProvider = T.Provider;

export function Tip({
  label,
  children,
  side = "bottom",
}: {
  label: ReactNode;
  children: ReactNode;
  side?: "top" | "bottom" | "left" | "right";
}) {
  return (
    <T.Root delayDuration={350}>
      <T.Trigger asChild>{children}</T.Trigger>
      <T.Portal>
        <T.Content
          side={side}
          sideOffset={6}
          className="z-[80] animate-fade-in rounded-md border border-border-strong bg-surface px-2 py-1 text-[12px] font-medium text-fg shadow-[0_8px_24px_-8px_rgba(0,0,0,0.45)]"
        >
          {label}
        </T.Content>
      </T.Portal>
    </T.Root>
  );
}
